import { createClient } from '@supabase/supabase-js';
import type { Database } from '../types/supabase';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  console.warn('Missing Supabase environment variables. Running in offline mode.');
}

// Create a more resilient Supabase client with better error handling
export const supabase = supabaseUrl && supabaseAnonKey ? createClient<Database>(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true
  },
  global: {
    headers: {
      'apikey': supabaseAnonKey
    }
  }
}) : null;

// Helper function to check if Supabase is available with better error handling
export const isSupabaseAvailable = async (): Promise<boolean> => {
  if (!supabase) return false;
  
  try {
    // Use a simpler health check
    const { error } = await supabase.from('products').select('id').limit(1).maybeSingle();
    return !error;
  } catch (error) {
    console.warn('Supabase availability check failed:', error);
    return false;
  }
};

// Enhanced helper function to safely execute Supabase operations
export const safeSupabaseOperation = async <T>(
  operation: () => Promise<T>,
  fallback?: T
): Promise<{ data: T | null; error: Error | null; isOffline: boolean }> => {
  if (!supabase) {
    return { data: fallback || null, error: new Error('Supabase not configured'), isOffline: true };
  }

  try {
    const data = await operation();
    return { data, error: null, isOffline: false };
  } catch (error) {
    console.warn('Supabase operation failed:', error);
    
    // Enhanced network error detection
    const isNetworkError = error instanceof Error && (
      error.message.includes('fetch') ||
      error.message.includes('network') ||
      error.message.includes('Failed to fetch') ||
      error.message.includes('Request timeout') ||
      error.message.includes('NetworkError') ||
      error.message.includes('ERR_NETWORK') ||
      error.message.includes('ERR_INTERNET_DISCONNECTED') ||
      error.message.includes('ERR_CONNECTION_REFUSED') ||
      error.name === 'AbortError' ||
      error.name === 'NetworkError' ||
      error.name === 'TypeError' ||
      // Check for common network-related status codes
      (error as any)?.status === 0 ||
      (error as any)?.code === 'NETWORK_ERROR'
    );
    
    return { 
      data: fallback || null, 
      error: error instanceof Error ? error : new Error('Unknown error'), 
      isOffline: isNetworkError 
    };
  }
};

// Retry mechanism for critical operations
export const retryOperation = async <T>(
  operation: () => Promise<T>,
  maxRetries: number = 3,
  delay: number = 1000
): Promise<T> => {
  let lastError: Error;
  
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      return await operation();
    } catch (error) {
      lastError = error instanceof Error ? error : new Error('Unknown error');
      
      if (attempt === maxRetries) {
        throw lastError;
      }
      
      // Wait before retrying, with exponential backoff
      await new Promise(resolve => setTimeout(resolve, delay * attempt));
    }
  }
  
  throw lastError!;
};